/*
  The shared select.

  Three separate tasks need one — the stacked-mode sort control below, FilterGradeDDL
  (task 8) and the nine selects in SearchCardsAdvanced (task 16) — so it is built once
  here rather than three times.

  A native <select>, not a listbox built out of divs. Keyboard support, type-ahead, the
  mobile wheel picker and screen-reader announcement all come free and are exactly the
  things a hand-rolled replacement gets wrong. Only the chrome is ours: appearance-none
  plus our own chevron.
*/

import { ChangeEvent, useRef } from "react";
import { ChevronDown } from "lucide-react";

import { CONTROL_BASE, CONTROL_LABEL, FOCUS_RING } from "components/styles";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  /* Always required: a select with no label is a select nobody can identify. */
  label: string;
  value: string;
  options: readonly SelectOption[];
  onChange: (value: string) => void;
  /* Supply one to keep it stable across renders; otherwise one is generated. */
  id?: string;
  name?: string;
  /*
    A disabled first option shown while nothing is chosen — "All grades", say. It is not
    selectable, so it cannot be re-chosen once the visitor has moved off it.
  */
  placeholder?: string;
  /* Renders the label sr-only. It is still the select's accessible name. */
  hideLabel?: boolean;
  disabled?: boolean;
  /* Layout for the wrapper: widths, flex behaviour. */
  className?: string;
  /* id of an element describing the control, e.g. a hint or an error. */
  describedBy?: string;
}

/*
  React 17 has no useId. A module counter behind a ref gives each instance one stable id
  for the life of the component, which is all label/htmlFor needs.
*/
let selectInstanceCount = 0;

const useSelectId = (provided?: string): string => {
  const generated = useRef<string>();
  if (!generated.current) {
    selectInstanceCount += 1;
    generated.current = `pcg-select-${selectInstanceCount}`;
  }
  return provided ?? generated.current;
};

const Select = (props: SelectProps) => {
  const {
    label,
    value,
    options,
    onChange,
    id,
    name,
    placeholder,
    hideLabel = false,
    disabled = false,
    className = "",
    describedBy,
  } = props;

  const selectId = useSelectId(id);

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    onChange(event.target.value);
  };

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <label htmlFor={selectId} className={hideLabel ? "sr-only" : CONTROL_LABEL}>
        {label}
      </label>
      <div className="relative">
        <select
          id={selectId}
          name={name ?? selectId}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          aria-describedby={describedBy}
          className={`w-full appearance-none py-2 pl-3 pr-9 ${CONTROL_BASE} ${FOCUS_RING}`}
        >
          {placeholder ? (
            <option value="" disabled>
              {placeholder}
            </option>
          ) : null}
          {/*
            Keyed by position, not by value: v1 /gameInfo lists the same setName more than
            once for a dozen games (six times for Pokemon alone — "S&V: 151",
            "MEGA: Perfect Order", "Nintendo: Black Star Promos"...), so Set Name's options
            genuinely collide on both value and label and React logged a duplicate-key
            warning on every page that mounted the panel. An option list is replaced
            wholesale rather than reordered, so the index is a stable identity here — the
            same idiom Breadcrumbs uses.
          */}
          {options.map((option, index) => (
            <option
              key={`${index}-${option.value}`}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>
        {/* Decorative: the select already announces itself as a combo box. */}
        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-pcg-textMuted"
        />
      </div>
    </div>
  );
};

export default Select;
